import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, ShieldAlert, Circle, Loader2 } from "lucide-react";
import {
  mentorReviewerQueue, mentorGetSubmission, mentorReviewDecision,
} from "../../lib/api";

const AMPEL = {
  GRUEN: { c: "text-emerald-300 border-emerald-400/40 bg-emerald-500/10", label: "GRÜN" },
  GELB:  { c: "text-amber-300 border-amber-400/40 bg-amber-500/10",  label: "GELB" },
  ROT:   { c: "text-rose-300 border-rose-400/40 bg-rose-500/10",   label: "ROT"  },
};

const CRITICAL_CODES = [
  "IGNORED_STOP_SIGNAL", "PUSHED_INTO_DEEPENING", "MADE_DIAGNOSIS",
  "PROMISED_OUTCOME", "CONFIRMED_MEMORY_AS_FACT", "SUGGESTIVE_NARRATIVE",
  "UNSOLICITED_TOUCH", "INTENSITY_MANUFACTURED", "CONTINUED_AFTER_RED_FLAG",
  "TRIVIALISED_ACUTE_DANGER", "MISSED_ESCALATION_PATH",
  "MISSED_CLOSURE_OR_AFTERCARE", "SEVERE_CONFIDENTIALITY_BREACH",
  "OUT_OF_SCOPE_TREATMENT", "UNAUTHORIZED_CLIENT_DATA_UPLOAD",
];

function trafficLight(k, p, safety, critical) {
  if (critical) return "ROT";
  if (Object.values(safety || {}).some(v => v === false)) return "ROT";
  const ks = k ?? 0, ps = p ?? 0;
  if (ks < 60 || ps < 60) return "ROT";
  if (ks >= 80 && ps >= 80) return "GRUEN";
  return "GELB";
}

function ReviewDialog({ sub, onClose, onDone }) {
  const [detail, setDetail] = useState(null);
  const [decision, setDecision] = useState("REVISION_REQUESTED");
  const [feedback, setFeedback] = useState("");
  const [reviewerNote, setReviewerNote] = useState("");
  const [knowledge, setKnowledge] = useState(80);
  const [practice, setPractice] = useState(80);
  const [safetyConsent, setSafetyConsent] = useState(true);
  const [safetyAnon, setSafetyAnon] = useState(true);
  const [criticalOn, setCriticalOn] = useState(false);
  const [criticalCode, setCriticalCode] = useState(CRITICAL_CODES[0]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    mentorGetSubmission(sub.id).then(setDetail).catch(e => setErr(String(e)));
  }, [sub.id]);

  const safety = { consent: safetyConsent, anonymisation: safetyAnon };
  const ampel = trafficLight(knowledge, practice, safety, criticalOn);
  const canApprove = ampel === "GRUEN" && !criticalOn;

  async function submit() {
    setBusy(true); setErr("");
    try {
      const body = {
        decision,
        feedback,
        knowledge_score: Number(knowledge),
        practice_score: Number(practice),
        safety_fields: safety,
        critical_violation: criticalOn,
        critical_violation_code: criticalOn ? criticalCode : null,
        reviewer_note: reviewerNote || null,
      };
      await mentorReviewDecision(sub.id, body);
      onDone();
    } catch (e) {
      const msg = e?.response?.data?.detail || String(e);
      setErr(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally { setBusy(false); }
  }

  const meta = AMPEL[ampel];
  const currentVersion = detail?.versions?.find(v => v.is_current);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-6 overflow-y-auto"
         data-testid="mentor-review-dialog">
      <div className="max-w-3xl w-full bg-[#141822] border border-[#D4AF37]/30 rounded-lg p-6 mt-8 mb-16 text-[#F5EFE7]">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37]/70 mb-1">Reviewer-Entscheidung</div>
            <h2 className="text-xl font-serif">{sub.assignment_key}</h2>
            <div className="text-xs text-white/50 mt-1">Version {sub.current_version} · Status {sub.status}</div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="close-review-dialog">×</button>
        </div>

        {detail?.versions && (
          <div className="border border-white/10 rounded p-3 mb-4 max-h-64 overflow-y-auto">
            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Aktuelle Version</div>
            <div className="text-sm whitespace-pre-wrap text-white/80">
              {currentVersion?.text_response || <span className="text-white/40">— kein Text —</span>}
            </div>
            {currentVersion?.learner_reflection && (
              <div className="mt-3 text-sm">
                <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Learner-Reflexion</div>
                <div className="text-white/70 whitespace-pre-wrap">{currentVersion.learner_reflection}</div>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 mb-4">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-white/50">Wissens-Score (0–100)</span>
            <input type="number" min="0" max="100" value={knowledge}
              onChange={e => setKnowledge(e.target.value)}
              data-testid="knowledge-score-input"
              className="mt-1 w-full bg-black/40 border border-white/20 rounded px-2 py-1 text-sm" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-white/50">Praxis-Score (0–100)</span>
            <input type="number" min="0" max="100" value={practice}
              onChange={e => setPractice(e.target.value)}
              data-testid="practice-score-input"
              className="mt-1 w-full bg-black/40 border border-white/20 rounded px-2 py-1 text-sm" />
          </label>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-4 text-sm">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={safetyConsent} onChange={e => setSafetyConsent(e.target.checked)}
              data-testid="safety-consent-check" />
            <span>Zustimmung dokumentiert</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={safetyAnon} onChange={e => setSafetyAnon(e.target.checked)}
              data-testid="safety-anonymisation-check" />
            <span>Anonymisierung erfüllt</span>
          </label>
        </div>

        <div className="border border-rose-400/30 bg-rose-500/5 rounded p-3 mb-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={criticalOn} onChange={e => setCriticalOn(e.target.checked)}
              data-testid="critical-violation-check" />
            <ShieldAlert size={14} className="text-rose-300" />
            <span className="text-rose-200">Kritischer, nicht kompensierbarer Fehler</span>
          </label>
          {criticalOn && (
            <select value={criticalCode} onChange={e => setCriticalCode(e.target.value)}
              data-testid="critical-code-select"
              className="mt-2 w-full bg-black/40 border border-rose-400/40 rounded px-2 py-1 text-sm">
              {CRITICAL_CODES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
        </div>

        <div className="flex items-center gap-2 mb-4">
          <span className="text-[10px] uppercase tracking-widest text-white/50">Ampel:</span>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] tracking-widest uppercase border ${meta.c}`}
                data-testid="review-ampel">
            <Circle size={10} className="fill-current" />
            {meta.label}
          </span>
        </div>

        <label className="block mb-3">
          <span className="text-[10px] uppercase tracking-widest text-white/50">Feedback an Lernenden</span>
          <textarea value={feedback} onChange={e => setFeedback(e.target.value)} rows={4}
            data-testid="review-feedback-input" placeholder="Konkrete Rückmeldung, wertschätzend, klar."
            className="mt-1 w-full bg-black/40 border border-white/20 rounded px-2 py-1 text-sm" />
        </label>

        <label className="block mb-4">
          <span className="text-[10px] uppercase tracking-widest text-white/50">Interne Reviewer-Notiz (privat)</span>
          <textarea value={reviewerNote} onChange={e => setReviewerNote(e.target.value)} rows={2}
            data-testid="reviewer-note-input"
            className="mt-1 w-full bg-black/40 border border-white/20 rounded px-2 py-1 text-sm" />
        </label>

        <div className="flex items-center gap-2 mb-4">
          <span className="text-[10px] uppercase tracking-widest text-white/50">Entscheidung:</span>
          <select value={decision} onChange={e => setDecision(e.target.value)}
            data-testid="decision-select"
            className="bg-black/40 border border-white/20 rounded px-2 py-1 text-sm">
            <option value="APPROVED" disabled={!canApprove}>
              APPROVED{!canApprove ? " (nur bei GRÜN + kein kritischer Fehler)" : ""}
            </option>
            <option value="REVISION_REQUESTED">REVISION_REQUESTED</option>
            <option value="REJECTED">REJECTED</option>
          </select>
        </div>

        {err && <div className="text-rose-300 text-sm mb-3" data-testid="review-error">{err}</div>}

        <div className="flex items-center justify-end gap-2">
          <button onClick={onClose}
            className="px-4 py-2 rounded border border-white/20 text-white/70 hover:text-white text-sm">
            Abbrechen
          </button>
          <button onClick={submit} disabled={busy || !feedback.trim()}
            data-testid="submit-review-btn"
            className="px-4 py-2 rounded bg-[#D4AF37] text-black hover:bg-[#c19f2a] disabled:opacity-40 text-sm inline-flex items-center gap-2">
            {busy && <Loader2 size={14} className="animate-spin" />}
            <ShieldCheck size={14} />
            Entscheidung speichern
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MentorReviewQueue() {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(null);

  async function load() {
    setLoading(true); setErr("");
    try {
      const data = await mentorReviewerQueue();
      setQueue(data.queue || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || String(e));
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-[#0B0F1A] text-[#F5EFE7] pt-24" data-testid="mentor-review-queue">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <Link to="/academy/dashboard" className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white/80 mb-6">
          <ArrowLeft size={14} /> Zurück
        </Link>
        <div className="mb-8">
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37]/70 mb-1">SAT MENTOR · Reviewer</div>
          <h1 className="text-3xl font-serif">Reviewer-Warteschlange</h1>
          <p className="text-sm text-white/50 mt-2 max-w-2xl">
            Vertraulich. Jede Entscheidung wird protokolliert. Ein kritischer, nicht
            kompensierbarer Fehler kann nicht durch Punkte überschrieben werden.
          </p>
        </div>

        {loading && <div className="text-white/50 text-sm">Lade …</div>}
        {err && <div className="text-rose-300 text-sm">{err}</div>}
        {!loading && !err && queue.length === 0 && (
          <div className="text-white/50 text-sm border border-white/10 rounded p-6" data-testid="review-queue-empty">
            Aktuell keine offenen Einreichungen.
          </div>
        )}

        <div className="space-y-3">
          {queue.map(s => (
            <div key={s.id}
                 className="border border-white/10 rounded p-4 hover:border-[#D4AF37]/40 transition"
                 data-testid={`queue-item-${s.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-[10px] tracking-widest uppercase text-white/40 mb-1">
                    {s.status} · v{s.current_version}
                  </div>
                  <div className="text-lg font-serif truncate">{s.assignment_key}</div>
                  <div className="text-xs text-white/40 mt-1">
                    User {s.user_id.slice(0, 8)}… · eingereicht {s.submitted_at ? new Date(s.submitted_at).toLocaleString("de-CH") : "—"}
                  </div>
                </div>
                <button onClick={() => setSelected(s)}
                  data-testid={`open-review-${s.id}`}
                  className="px-3 py-1.5 rounded border border-[#D4AF37]/50 text-[#D4AF37] text-xs hover:bg-[#D4AF37]/10">
                  Reviewen
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selected && (
        <ReviewDialog
          sub={selected}
          onClose={() => setSelected(null)}
          onDone={() => { setSelected(null); load(); }}
        />
      )}
    </div>
  );
}
